/**
 * AIModeContext — the single source of truth for the AI's posture (spectrum
 * mode) and active persona across the whole frontend.
 *
 * Before this context, the header control bar, the AI Cockpit page, and the
 * Chat view each kept their own `useState` copy, synchronised only through
 * `localStorage['aiMode']` + a same-tab `CustomEvent('aimode-change')`. That
 * drifted: Chat.jsx read the key with the OLD vocabulary (off/advisor/
 * autonomous) and never normalised, so when the header wrote spectrum strings
 * ('advising'/'acting') Chat's branches silently went dead. There was also no
 * echo-back from the desktop, so the posture could be stale after a reconnect.
 *
 * This context centralises all of that:
 *   - ONE `mode` + `persona` value every surface reads/writes.
 *   - The desktop is the source of truth: on connect we adopt the desktop's
 *     current posture, and we live-adopt the `chat.ai_mode_changed` broadcast.
 *   - `setMode`/`setPersona` do the full dispatch (set_global_ai_mode on the
 *     sacred-timeline feature + setAiMode on the chat feature) in one place.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useWebRTC } from './WebRTCContext';
import {
    SPECTRUM_META,
    SPECTRUM_TO_CHAT_MODE,
    normalizeToSpectrum,
} from '../constants/aiModes';

const AIModeContext = createContext(null);

const MODE_KEY = 'aiMode';
const PERSONA_KEY = 'aiPersonality';
const DEFAULT_PERSONA = 'dominatrix';

// Race a promise against a timeout so a request that hangs (data channel not
// ready yet) becomes a retry instead of blocking the adoption loop forever.
const withTimeout = (p, ms) =>
    Promise.race([p, new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms))]);

export function AIModeProvider({ children }) {
    const { isConnected, connectedPeers, featureManager } = useWebRTC();

    const [mode, setModeRaw] = useState(() => normalizeToSpectrum(localStorage.getItem(MODE_KEY)));
    const [persona, setPersonaRaw] = useState(() => localStorage.getItem(PERSONA_KEY) || DEFAULT_PERSONA);

    // Refs so the memoised setters never capture a stale mode/persona.
    const modeRef = useRef(mode);
    const personaRef = useRef(persona);

    // Persist + broadcast a same-tab event for any legacy listener not yet
    // migrated to this context (belt-and-suspenders; the context is the real
    // sync path now).
    useEffect(() => {
        modeRef.current = mode;
        localStorage.setItem(MODE_KEY, mode);
        window.dispatchEvent(new CustomEvent('aimode-change', { detail: mode }));
    }, [mode]);

    useEffect(() => {
        personaRef.current = persona;
        localStorage.setItem(PERSONA_KEY, persona);
    }, [persona]);

    const pushChatMode = useCallback((spectrum, personaId) => {
        const chat = featureManager?.getFeature?.('chat');
        if (!chat) return;
        const chatMode = SPECTRUM_TO_CHAT_MODE[spectrum] || 'human';
        chat.setAiMode?.(chatMode, spectrum !== 'dormant' ? personaId : null);
    }, [featureManager]);

    // ── Public setters ──────────────────────────────────────────────────────
    const setMode = useCallback((next) => {
        const spectrum = normalizeToSpectrum(next);
        setModeRaw(spectrum);
        const sacred = featureManager?.getFeature?.('sacred-timeline');
        // Always push — INCLUDING 'dormant' — so the desktop can never retain a
        // stale advising/acting posture the controller has turned off.
        sacred?.sendMessage?.('set_global_ai_mode', { mode: spectrum });
        pushChatMode(spectrum, personaRef.current);
    }, [featureManager, pushChatMode]);

    const setPersona = useCallback((id) => {
        setPersonaRaw(id);
        personaRef.current = id;
        pushChatMode(modeRef.current, id);
    }, [pushChatMode]);

    // ── Adopt the desktop's truth on (re)connect ────────────────────────────
    // The desktop is the source of truth; the frontend only displays it. The
    // ORIGINAL bug: this fired once on isConnected, the getGlobalAiMode() query
    // failed because the data channel wasn't usable yet, and it silently kept
    // the stale localStorage paint with NO retry (observed: cockpit stuck on
    // "Advising" while the desktop was actually off/dormant). The real fix is
    // the retry loop below. We trigger on isConnected OR connectedPeers changing
    // — NOT connectedPeers alone, since the WebRTC layer warns that state can
    // get out of sync; the retry tolerates a not-yet-ready channel either way.
    useEffect(() => {
        if (!featureManager || (!isConnected && connectedPeers.length === 0)) return;
        let cancelled = false;
        (async () => {
            // Retry the WHOLE lookup+query: the sacred-timeline feature registers
            // asynchronously AFTER the connection signals flip true, so it may not
            // exist on the first effect run — and no dependency changes when it
            // later registers. Looking it up once before the loop (and bailing)
            // silently kept the stale paint even on a healthy connection (caught
            // live: isConnected/connectedPeers both true, feature just not yet
            // registered). Retrying the lookup fixes that.
            for (let attempt = 0; attempt < 10 && !cancelled; attempt++) {
                try {
                    const sacred = featureManager.getFeature?.('sacred-timeline');
                    if (sacred?.getGlobalAiMode) {
                        const desktopMode = await withTimeout(sacred.getGlobalAiMode(), 2000);
                        if (cancelled) return;
                        if (desktopMode) {
                            const next = normalizeToSpectrum(desktopMode);
                            // Only sync our own display. Do NOT push it back
                            // (setMode has side effects like re-scheduling
                            // initiations, which would re-deliver the pending
                            // message on every reconnect).
                            setModeRaw((prev) => (prev === next ? prev : next));
                            return;
                        }
                    }
                } catch {
                    /* feature not ready / channel not ready / timed out — retry */
                }
                await new Promise((r) => setTimeout(r, 500));
            }
        })();
        return () => { cancelled = true; };
    }, [isConnected, connectedPeers.length, featureManager]);

    // ── Live echo-back: adopt any posture change broadcast by the desktop ────
    // Covers other tabs/clients and any desktop-initiated change. Our own
    // setMode round-trips through here too; the same-value guard stops a loop.
    // isConnected is in the deps so we (re)subscribe once the sacred-timeline
    // feature has actually registered (it isn't present before connection).
    useEffect(() => {
        if (!featureManager) return;
        const sacred = featureManager.getFeature?.('sacred-timeline');
        if (!sacred?.on) return;
        const off = sacred.on('sacred_event', (event) => {
            if (event?.type !== 'chat.ai_mode_changed') return;
            const spec = event?.data?.spectrum;
            if (!spec) return;
            const next = normalizeToSpectrum(spec);
            setModeRaw((prev) => (prev === next ? prev : next));
        });
        return typeof off === 'function' ? off : undefined;
    }, [featureManager, isConnected]);

    const value = {
        mode,
        setMode,
        persona,
        setPersona,
        meta: SPECTRUM_META[mode] || SPECTRUM_META.dormant,
        isConnected,
    };

    return <AIModeContext.Provider value={value}>{children}</AIModeContext.Provider>;
}

export function useAIMode() {
    const context = useContext(AIModeContext);
    if (!context) {
        throw new Error('useAIMode must be used within an AIModeProvider');
    }
    return context;
}
